// @ts-check
import {
  BACKGROUND_CHECK_VALUES,
  BackgroundCheckToLevelEnum,
  DrugQualificationsEnum,
  QUALIFICATION,
  SCREENING
} from "../component-templates/certification-badge/constants";

/**
 * @typedef {import('../component-templates/certification-icon/utils.js').Certification} Certification
 * @typedef {import('../components/roster/types.js').CertificationBadge} CertificationBadge
 * @typedef {import("./users").ProcessedUserQualification} ProcessedUserQualification
 */

export const DrugScreenQualificationHierarchy = {
  [DrugQualificationsEnum["5 Panel No THC Drug Screen"]]: 1,
  [DrugQualificationsEnum["5 Panel Drug Screen"]]: 2,
  [DrugQualificationsEnum["6 Panel Drug Screen"]]: 3,
  [DrugQualificationsEnum["10 Panel Drug Screen"]]: 4,
}

export const LegacyCertifications =  Object.freeze({
  "Clean Motor Vehicle Record": "1",
  "Passed Drug Screen": "2",
  "Food Handlers Card": "3",
  "Orientation Complete": "4",
  "Event Certified": "5",
  "Forklift Certification": "6",
  "Bartender Certified": "7",
  "Guard Card": "8",
  "Cashier Trained": "9",
  "Military": "10",
  "Alcohol Server Certification": "13",
  "OSHA Certified": "14",
  "US DOT": "15",
  "Alcohol Servers (21+)": "16",
  "Responsible Beverage Server Certification (California Only)": "17",
  "Passed 5 Panel no THC Drug Screen": "18",
  "Passed 5 Panel Drug Screen": "19",
  "Passed 10 Panel Drug Screen": "20",
  "Passed 6 Panel Drug Screen": "21",
  "Creating Legends Training": "22"
});

export class CertQualificationUtils {

  static ignoredCertificationIds = Object.freeze([
    LegacyCertifications["Passed Drug Screen"],  // Legacy drug screening cert
  ]);

  static isLegacyCertVerified(certification) {
    return certification.verified && certification.verifiedBy && certification.completedOn
  }

  /**
   * Filters out legacy certifications that are no longer used or have multiple records for the same qualification.
   *
   * @param certifications {Certification[]} - array of certifications
   * @returns {Certification[]} certifications with any ignored and duplicate certifications removed
   */
  static filterLegacyCerts(certifications) {

    const filteredCertifications = certifications.filter(cert => {
      return !this.ignoredCertificationIds.includes(cert.certificateId)
    });

    // Group certifications by certificateId and only keep the verified ones.
    const groupedCertifications = filteredCertifications.reduce((acc, cert) => {
      if (!acc[cert.certificateId]) {
        acc[cert.certificateId] = [];
      }

      if (this.isLegacyCertVerified(cert)) {
        acc[cert.certificateId].push(cert);
      }
      return acc;
    }, {});

    // For each group, keep the most recent certification
    return Object.values(groupedCertifications).map(group => {
      if (group.length === 0) return null;
      return group.reduce((mostRecent, cert) => {
        return new Date(cert.verifiedAt) > new Date(mostRecent.verifiedAt) ? cert : mostRecent;
      });
    }).filter(cert => cert !== null)
  }

  static getDrugScreenLevel(qualification) {
    return DrugScreenQualificationHierarchy[qualification.qualificationId];
  }

  static getBackgroundCheckLevel(qualification) {
    return qualification.details.level;
  }

    /**
     * Returns the highest level drug screen qualification that the user has passed.
     *
     * @param {ProcessedUserQualification[]} qualifications 
     * @returns {ProcessedUserQualification | null}
     */
    static getHighestDrugScreeningQualification(qualifications) {
      return qualifications.reduce((highest, qualification) => {
        if (qualification.qualificationType === QUALIFICATION.TYPES.DRUG_SCREEN
          && qualification.status === "COMPLETE"
          && (!highest || this.getDrugScreenLevel(qualification) > this.getDrugScreenLevel(highest))) {
          return qualification;
        }
        return highest;
      }, null);
    }

    /**
     * Returns the highest level background check qualification that the user has passed. Excludes any background checks
     * that are below the basic criminal background check with three aliases (Level 2).
     *
     * @param {ProcessedUserQualification[]} qualifications 
     * @returns {ProcessedUserQualification | null}
     */
    static getHighestBackgroundCheckQualification(qualifications) {
      return qualifications.reduce((highest, qualification) => {
        if (qualification.qualificationType === QUALIFICATION.TYPES.BACKGROUND_CHECK
          && qualification.status === "COMPLETE"
          && qualification.details.level >= BACKGROUND_CHECK_VALUES.BASIC_CRIMINAL_WITH_THREE_ALIASES
          && (!highest || this.getBackgroundCheckLevel(qualification) > this.getBackgroundCheckLevel(highest))) {
          return qualification;
        }
        return highest;
      }, null);
    }

  /**
   * @param {string} dateString Datetime string given in the format "2021-09-01T00:00:00.000Z"
   * @returns {string|null} Formatted date string in the format "MM/DD/YYYY" or null if the input is invalid
   */
  static formatDate(dateString) {
    const date = new Date(dateString);
    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
  }

  /**
   * @param {Certification[]} certifications
   * @returns {CertificationBadge[]}
   *
   * Given a list of fetched certifications, formats them into a list of CertificationBadges.
   * Takes into account the type of certification and formats the tooltip and title accordingly.
   */
  static formatCertifications(certifications) {
    return certifications.map((certificate) => {
      let toolTipDate = undefined;

      const title = certificate.certificateName;
      const toolTip = `${certificate.certificateName}`;
      if (certificate.completedOn) {
        const formattedDate = this.formatDate(certificate.completedOn);
        toolTipDate = `(issued on ${formattedDate}`;
      }

      return {
        icon: certificate.badgeIcon,
        toolTip,
        toolTipDate,
        title,
      };
    });
  }

  /**
   * @param {ProcessedUserQualification[]} qualifications
   * @returns {CertificationBadge[]}
   */
  static formatUserQualificationsToCerts(qualifications) {
    const userCerts = [];
    const highestBackgroundCheckQualification = this.getHighestBackgroundCheckQualification(qualifications);
    if (highestBackgroundCheckQualification) {
      userCerts.push(this._qualificationToCert(highestBackgroundCheckQualification));
    }

    const highestDrugScreeningQualification = this.getHighestDrugScreeningQualification(qualifications);
    if (highestDrugScreeningQualification) {
      userCerts.push(this._qualificationToCert(highestDrugScreeningQualification));
    }
    return userCerts;
  }


  /**
   * @param {ProcessedUserQualification} qualification
   * @returns {{ toolTipText: string, toolTipDateText: string}} Tooltip text for the qualification
   */
  static _getQualificationTooltipDetails(qualification) {
    const verifiedDate = this.formatDate(qualification.verification.verifiedAt);
    let toolTipText;
    let toolTipDateText;
    switch (qualification.qualificationType) {
      case QUALIFICATION.TYPES.BACKGROUND_CHECK:
        const tooltipMappingIndex = BackgroundCheckToLevelEnum[qualification.qualificationId];
        toolTipText = SCREENING.BACKGROUND.CERT_STRINGS.tooltipMapping[tooltipMappingIndex];
        toolTipDateText = `(issued on ${verifiedDate})`;
        break;
      case QUALIFICATION.TYPES.DRUG_SCREEN:
        toolTipText = qualification.qualificationName;
        toolTipDateText = `(issued on ${verifiedDate})`;
        break;
      default:
        toolTipText = "";
        toolTipDateText = "";
    }
    return {toolTipText, toolTipDateText};
  }

  /**
   * @param {ProcessedUserQualification} qualification
   * @returns {string} title of the badge 
   */
  static _getQualificationTitle(qualification) {
    switch (qualification.qualificationType) {
      case QUALIFICATION.TYPES.BACKGROUND_CHECK:
        return `Level ${qualification.details.level} background check passed`;
      case QUALIFICATION.TYPES.DRUG_SCREEN:
        return qualification.qualificationName;
      default:
        return ""
    }
  }

  /**
   * @param {ProcessedUserQualification} qualification
   * @returns {CertificationBadge}
   */
  static _qualificationToCert(qualification) {
    const { toolTipText, toolTipDateText } = this._getQualificationTooltipDetails(qualification);

    return {
      icon: qualification.icon,
      toolTip: toolTipText,
      toolTipDate: toolTipDateText,
      title: this._getQualificationTitle(qualification),
    }
  }
}
